import React, { useMemo, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-5';

import { Button } from 'components/ui/button';
import TemplateForm from './template_form';
import { updateDataExportTemplate } from '../../helpers/prospect_api';
import type {
  DataExportTemplate,
  DataExportTemplateColumnOptions,
  DataExportTemplateForm
} from 'bundles/connect/types';

type Props = {
  columnOptions: DataExportTemplateColumnOptions;
  template?: DataExportTemplate;
  onHide: () => void;
  onTemplateUpdated: (template: DataExportTemplate) => void;
}

export default function EditTemplateModal({ columnOptions, template, onHide, onTemplateUpdated }: Props) {
  const [formData, setFormData] = useState<DataExportTemplateForm>({ name: '' });
  const [selectedColumns, setSelectedColumns] = useState<{value: string; label: string;}[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({ name: template.name, dataExportType: template.dataExportType });

      const sourceOptions = columnOptions[template.dataExportType] || [];

      const columns: { value: string; label: string }[] = [];
      template.columns.forEach((colValue) => {
        const found = sourceOptions.find(opt => opt.value === colValue);
        if (found) columns.push(found);
      });

      setSelectedColumns(columns);
    } else {
      setFormData({ name: '' });
      setSelectedColumns([]);
    }
  }, [template]);

  const handleSave = () => {
    if (!template) return;
    setLoading(true);
    const params = {
      ...formData,
      columns: selectedColumns.map(c => c.value)
    };

    updateDataExportTemplate(template.id, params).then((response) => {
      setLoading(false);
      onHide();
      onTemplateUpdated(response.dataExportTemplate);
    });
  };

  const canSubmit = useMemo(() => {
    if (loading) return false;

    return formData.name.length > 2 && selectedColumns.length > 0;
  }, [loading, formData.name, selectedColumns]);

  return (
    <Modal show={!!template} onHide={onHide}>
      <Modal.Header className="flex-row" closeButton>
        <Modal.Title>Edit Data Export Template</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TemplateForm
          columnOptions={columnOptions}
          disableTypeSelector
          formData={formData}
          selectedColumns={selectedColumns}
          setFormData={setFormData}
          setSelectedColumns={setSelectedColumns}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={loading} label="Cancel" variant="outline" onClick={onHide} />
        <Button disabled={!canSubmit} label="Save" variant="secondary" onClick={handleSave} />
      </Modal.Footer>
    </Modal>
  );
}
